'use client';

import { buildFaqPageJsonLd } from './FaqPageJsonLd';
import type { AccordionItemResolved, AccordionResolved } from './types';
import { Accordion, type AccordionItemData } from '@/components/Accordion';
import { CtaActionButton } from '@/components/CtaActionButton';
import { JsonLdScript } from '@/components/JsonLdScript';
import { useRenderContext } from '@/lib/render/RenderContextProvider';
import { RichText } from '@/lib/render/RichText';
import { getSectionColorClass } from '@/lib/section-colors';
import type { SectionBackgroundColor } from '@/lib/section-colors';

const toAccordionItems = (
  items: AccordionItemResolved[],
  // Titles are plain strings rendered directly, so they need substitution
  // here; item bodies get it from `RichText` itself.
  sub: (text: string) => string,
): AccordionItemData[] =>
  items.map((item) => ({
    id: item.id,
    title: sub(item.title),
    content: (
      <div className="[&_a]:text-primary [&_a]:underline">
        <RichText nodes={item.content ?? undefined} />
      </div>
    ),
  }));

export function AccordionComponent({
  resolved,
}: {
  resolved: AccordionResolved;
}) {
  const { substitutePlaceholders: sub } = useRenderContext();

  const { layout, items, itemsRight, introText, cta, isFaq } = resolved;
  // The double layout is one group spanning both columns, so JSON-LD and the
  // single-open rule both cover every item of this Accordion (AC 4, OQ-10).
  const allItems = layout === 'double' ? [...items, ...itemsRight] : items;

  if (allItems.length === 0) {
    return null;
  }

  // § Two visual modes — resolved once and passed to whichever layout renders.
  const filled = resolved.itemBackground !== 'none';
  const variant = filled ? 'filled' : 'separators';
  const itemBackgroundClass = filled
    ? getSectionColorClass(resolved.itemBackground as SectionBackgroundColor)
    : undefined;

  const jsonLd = isFaq ? buildFaqPageJsonLd(allItems, sub) : null;
  const accordionItems = toAccordionItems(allItems, sub);

  return (
    <div className="pt-4">
      {/* Client component, but Next.js server-renders it, so the script is in
          the initial HTML as 10.02.01 § AC 8 requires. */}
      {jsonLd && <JsonLdScript data={jsonLd} />}

      {layout === 'double' ? (
        <>
          {/* Intro text and CTA belong to the double layout alone — the other
              layouts ignore both fields even when an editor filled them in. */}
          {introText && <p className="mb-6 text-base">{sub(introText)}</p>}
          <Accordion
            items={accordionItems}
            splitAt={items.length}
            variant={variant}
            itemBackgroundClass={itemBackgroundClass}
          />
          {cta && (
            <div className="mt-8 flex justify-center">
              <CtaActionButton action={cta} sub={sub} />
            </div>
          )}
        </>
      ) : (
        <Accordion
          items={accordionItems}
          columns={
            layout === 'grid' ? (Number(resolved.columns) as 2 | 3 | 4) : 1
          }
          variant={variant}
          itemBackgroundClass={itemBackgroundClass}
        />
      )}
    </div>
  );
}
